import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { PromptSelect } from '../../Models/components/PromptSelect';

import { FormGroup } from '@/components/FormGroup';
import { RequiredLabel } from '@/components/RequiredLabel';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { useCreatePrompt } from '@/hooks/useCreatePrompt';
import { Prompt } from '@/services/PromptsService';

const clonePromptSchema = z.object({
  promptId: z.string().min(1, 'Prompt is required'),
  newPromptName: z.string().min(1, 'New prompt name is required'),
});

type ClonePromptFormData = z.infer<typeof clonePromptSchema>;

interface ClonePromptFormProps {
  open: boolean;
  onClose: () => void;
}

export function ClonePromptForm({ open, onClose }: ClonePromptFormProps) {
  const [selectedPrompt, setSelectedPrompt] = useState<Prompt | undefined>();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ClonePromptFormData>({
    resolver: zodResolver(clonePromptSchema),
    defaultValues: {
      promptId: '',
      newPromptName: '',
    },
  });

  const { createPrompt, isLoading } = useCreatePrompt();

  async function onSubmit(data: ClonePromptFormData) {
    try {
      if (!selectedPrompt) {
        toast.error('Please select a prompt to clone');
        return;
      }

      const promptData = {
        promptName: data.newPromptName,
        serviceName: selectedPrompt.serviceName,
        template: selectedPrompt.template,
        responseSchema: selectedPrompt.responseSchema,
        main: false,
      };

      await createPrompt(promptData);
      toast.success('Prompt cloned successfully!');
      reset();
      setSelectedPrompt(undefined);
      onClose();
    } catch (error) {
      toast.error(
        error instanceof AxiosError
          ? error.response?.data?.message
          : 'Failed to clone prompt. Please try again.',
      );
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose} modal={false}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Clone Prompt</DialogTitle>
          <DialogDescription>
            Select a prompt to clone and provide a new prompt name.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          <FormGroup error={{ message: errors.promptId?.message, font: 'sm' }}>
            <RequiredLabel htmlFor="promptId">Prompt</RequiredLabel>
            <Controller
              name="promptId"
              control={control}
              render={({ field }) => (
                <PromptSelect
                  value={field.value}
                  onChange={(promptId, prompt) => {
                    field.onChange(String(promptId));
                    setSelectedPrompt(prompt);
                  }}
                />
              )}
            />
          </FormGroup>

          <FormGroup
            error={{ message: errors.newPromptName?.message, font: 'sm' }}
          >
            <RequiredLabel htmlFor="newPromptName">
              New Prompt Name
            </RequiredLabel>
            <Controller
              name="newPromptName"
              control={control}
              render={({ field }) => (
                <Input {...field} placeholder="Enter new prompt name" />
              )}
            />
          </FormGroup>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                reset();
                setSelectedPrompt(undefined);
                onClose();
              }}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Cloning...' : 'Clone Prompt'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
